import React, { useState } from "react";
import { Typography, Button } from "../ui";
import { WaitlistForm, SuccessState, AppPreview, SessionSwitchLink } from "../features";
import { useWindowSize, useZoomAwareViewport } from "../../utils/hooks";
import { validateEmail, signupToWaitlist } from "../../utils";

const DesktopHeroLayout = ({
  hasJoined,
  queuePosition,
  onWaitlistSuccess,
  onReferFriend,
  referredBy,
  // Session switching (returning user on a friend's link / different email)
  hasExistingSession = false,
  onShowExisting,
  onUseDifferentEmail,
}) => {
  const { width: screenWidth } = useWindowSize();
  const zoomViewport = useZoomAwareViewport();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    if (emailError) {
      setEmailError(validateEmail(newEmail));
    }
  };

  const handleSubmit = async () => {
    const error = validateEmail(email);
    setEmailError(error);

    if (error) return;

    setIsLoading(true);
    try {
      const result = await signupToWaitlist(email.trim(), referredBy);
      if (result.success) {
        onWaitlistSuccess(result.data);
        setEmail("");
        setEmailError("");
      } else {
        setEmailError(
          result.error || "Something went wrong. Please try again.",
        );
      }
    } catch (error) {
      console.error("Signup error:", error);
      setEmailError("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Calculate scaling factor based on screen width relative to 1440px baseline
  const getScaleFactor = () => {
    if (screenWidth >= 1440) {
      return screenWidth / 1440;
    }
    // For screens smaller than 1440px but >= 1000px, use minimum scale
    return Math.max(screenWidth / 1440, 0.7);
  };

  const scaleFactor = getScaleFactor();

  // Image stack scales proportionally with screen width to maintain balance
  const getImageScaleFactor = () => {
    if (screenWidth >= 1440) {
      return screenWidth / 1440; // Scale up proportionally for screens above 1440px
    }
    // Only scale down slightly for screens between 1000-1440px
    return Math.max(0.85, screenWidth / 1440);
  };

  const imageScaleFactor = getImageScaleFactor();

  // Container-level zoom compensation using CSS transform
  const getContainerScale = () => {
    if (!zoomViewport.isZoomed || zoomViewport.zoomLevel <= 1) return 1;

    // Gentle counter-scaling to maintain proportions
    return 1 / Math.pow(zoomViewport.zoomLevel, 0.15);
  };

  const containerScale = getContainerScale();

  // Scaled dimensions based on 1440px specifications
  const dimensions = {
    imageStack: {
      width: Math.round(628 * imageScaleFactor),
      height: Math.round(511 * imageScaleFactor),
    },
    leftContainer: {
      width: Math.round(700 * scaleFactor),
    },
    paragraph: {
      width: Math.round(700 * scaleFactor),
    },
    emailInput: {
      maxWidth: "26.25rem", // 420px - Fixed max width for email input
      minWidth: `${Math.round(420 * (screenWidth >= 1440 ? 1 : scaleFactor))}px`, // Scales below 1440px
      height: "4rem", // 64px - Fixed height
    },
    button: {
      maxWidth: "12.875rem", // 206px - Fixed max width for join waitlist button
      minWidth: `${Math.round(206 * (screenWidth >= 1440 ? 1 : scaleFactor))}px`, // Scales below 1440px
      height: "4rem", // 64px - Fixed height
    },
    fontSize: {
      title: screenWidth >= 1440 ? 54 : Math.round(54 * scaleFactor), // No scaling above 1440px
      paragraph: screenWidth >= 1440 ? 36 : Math.round(36 * scaleFactor), // No scaling above 1440px
      button: screenWidth >= 1400 ? "1.5rem" : "1.2rem", // 24px above 1400px, 16px below to prevent wrapping
    },
    spacing: {
      titleLines: 0, // No gap between title lines
      titleToParagraph: Math.round(8 * scaleFactor), // 8px
      paragraphToForm: Math.round(8 * scaleFactor), // 8px - gap between paragraph and form
      inputToButton: Math.round(8 * scaleFactor), // 8px
    },
    borderRadius: screenWidth <= 1200 ? "0.75rem" : "1.25rem", // 12px at 1000-1200px, 20px at larger
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "calc(100vh - 6rem)", // Full viewport minus header space (96px)
        padding: "0 2rem",
        paddingTop: "6rem", // 96px - Header top (24px) + badge height (48px) + gap (24px)
        transform: `scale(${containerScale})`,
        transformOrigin: "center center",
        transition: "transform 0.2s ease-out",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "4rem",
          maxWidth: "1400px",
          width: "100%",
        }}
      >
        {/* Left Container - Hero Content */}
        <div
          style={{
            width: `${dimensions.leftContainer.width}px`,
            minHeight: "auto",
            display: "block",
          }}
        >
          {/* Title */}
          <div
            style={{ marginBottom: `${dimensions.spacing.titleToParagraph}px` }}
          >
            <div
              style={{
                fontFamily: "Montserrat, sans-serif",
                fontSize: `${dimensions.fontSize.title}px`,
                fontWeight: 600,
                color: "white",
                lineHeight: "1.2",
                marginBottom: `${dimensions.spacing.titleLines}px`,
              }}
            >
              Train Your Mind.
            </div>
            <div
              style={{
                fontFamily: "Montserrat, sans-serif",
                fontSize: `${dimensions.fontSize.title}px`,
                fontWeight: 600,
                color: "white",
                lineHeight: "1.2",
              }}
            >
              Dominate The Pitch.
            </div>
          </div>

          {/* Paragraph Container */}
          <div
            style={{
              maxWidth: "43.75rem", // 700px - Fixed width to maintain 4-line structure
              marginBottom: `${dimensions.spacing.paragraphToForm}px`,
            }}
          >
            <p
              style={{
                fontFamily: "Poppins, sans-serif",
                fontSize: `${dimensions.fontSize.paragraph}px`,
                fontWeight: 400,
                color: "white",
                lineHeight: "1.4",
                textAlign: "left",
                margin: 0,
              }}
            >
              The meditation app built exclusively for footballers. Master your
              mindset, find flow fast and perform, when it matters most.
            </p>
          </div>

          {/* Form Container */}
          <div style={{ maxWidth: "40rem" }}>
            {" "}
            {/* 640px - Form container max width */}
            {!hasJoined ? (
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: `${dimensions.spacing.inputToButton}px`,
                  }}
                >
                  {/* Email Input */}
                  <input
                    type="email"
                    placeholder="Enter Your Email"
                    value={email}
                    onChange={handleEmailChange}
                    onKeyPress={handleKeyPress}
                    disabled={isLoading}
                    style={{
                      maxWidth: dimensions.emailInput.maxWidth,
                      minWidth: dimensions.emailInput.minWidth,
                      width: "100%",
                      height: dimensions.emailInput.height,
                      padding: "0 1.5rem", // 24px
                      borderRadius: dimensions.borderRadius,
                      border: emailError
                        ? "0.5px solid #EF4444"
                        : "0.5px solid rgba(0, 0, 0, 0.4)",
                      backgroundColor: emailError ? "#FEF2F2" : "white",
                      fontSize: "1.25rem", // 20px
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 300,
                      color: "black",
                      outline: "none",
                    }}
                  />

                  {/* Submit Button */}
                  <button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    style={{
                      maxWidth: dimensions.button.maxWidth,
                      minWidth: dimensions.button.minWidth,
                      width: "auto",
                      height: dimensions.button.height,
                      padding: "0 1.5rem", // 24px - Prevents text wrapping
                      backgroundColor: isLoading ? "#D1D5DB" : "white",
                      color: "black",
                      border: "none",
                      borderRadius: dimensions.borderRadius,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: dimensions.fontSize.button,
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 600,
                      whiteSpace: "nowrap", // Prevents text wrapping
                      cursor: isLoading ? "not-allowed" : "pointer",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (!isLoading) {
                        e.target.style.backgroundColor = "#F3F4F6";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isLoading) {
                        e.target.style.backgroundColor = "white";
                      }
                    }}
                  >
                    {isLoading ? "Joining..." : "Join Waitlist"}
                  </button>
                </div>

                {/* Error Message */}
                {emailError && (
                  <div
                    style={{
                      marginTop: "0.5rem", // 8px
                      padding: "0.5rem 1rem", // 8px 16px
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                      borderRadius: "0.5rem", // 8px
                      color: "#EF4444",
                      fontSize: "0.875rem", // 14px
                      fontFamily: "Inter, sans-serif",
                      maxWidth: "40rem", // 640px - Matches form container width
                    }}
                  >
                    {emailError}
                  </div>
                )}

                {/* Returning user viewing a friend's referral link */}
                {hasExistingSession && (
                  <SessionSwitchLink onClick={onShowExisting}>
                    Already joined? View your referral link
                  </SessionSwitchLink>
                )}
              </div>
            ) : (
              /* Success State - Joined Badge + Refer Friend */
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: `${dimensions.spacing.inputToButton}px`,
                  }}
                >
                  {/* Joined Badge - replaces email input - Exact Figma specs */}
                  <div
                    style={{
                      width: `${Math.round(173.89 * (screenWidth >= 1440 ? 1 : scaleFactor))}px`, // Figma: 173.89px
                      height: "4rem", // 64px - Fixed height from Figma
                      backgroundColor: "#0D0E20", // Exact Figma color
                      color: "white",
                      borderRadius: "1.25rem", // 20px - Exact Figma border radius
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: `${screenWidth >= 1440 ? 25.28 : Math.round(25.28 * scaleFactor)}px`, // Exact Figma font size
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      gap: "0.5rem", // 8px gap from Figma
                      padding: `${Math.round(14.22 * (screenWidth >= 1440 ? 1 : scaleFactor))}px ${Math.round(28.44 * (screenWidth >= 1440 ? 1 : scaleFactor))}px`, // Exact Figma padding
                    }}
                  >
                    <span>Joined</span>
                    <svg
                      width="26"
                      height="26"
                      viewBox="0 0 26 26"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{
                        width: "1.625rem", // 26px exact from Figma
                        height: "1.625rem", // 26px exact from Figma
                      }}
                    >
                      <rect width="26" height="26" fill="url(#pattern0_tick)" />
                      <defs>
                        <pattern
                          id="pattern0_tick"
                          patternContentUnits="objectBoundingBox"
                          width="1"
                          height="1"
                        >
                          <use
                            href="#image0_tick"
                            transform="scale(0.0104167)"
                          />
                        </pattern>
                        <image
                          id="image0_tick"
                          width="96"
                          height="96"
                          preserveAspectRatio="none"
                          href="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGAAAABgCAYAAADimHc4AAAACXBIWXMAAAsTAAALEwEAmpwYAAABUklEQVR4nO3ZsWrCYBhG4VyUTg6dKh30jhw69mYLIpTwT0eiBLIVrc2b8J0HOjec95do0nWSJEmSJEmSJEmSJElLAHwAp/R1lATsgZ67r/T1VI4/coRgfEdYQHxHmOGG+1v8kTfmwMkfNeDw0guoDOPnGD/I+EHGDzJ+kPGDjB9kfOPXhD+yjF8Snnzjl4Qn3/gl4ck3/qyGV3LDK7yVvUbsl3DNfwZ8Tl7NHYPXsS/3GnESn+QIGD83AsbPjUDhG278JkfFG+6Tp+7lnwQqnvyljED1+MkRMH5uBIyfGwHj50bA+LkRMH5uBIyfGwHj50bA+LkRMH78eU1f7tnOf3vwVNd+vLCCEZrxcyM04+c+Cc34uRGa8XMjNOPnRmjGz/1O6P2ePxPgDbhM4v8A73P9f3W3EbbAN3AGdkYJADbDn/ElSZIkSZIkSZIkSZK6lbsCrnWmyCGcuTAAAAAASUVORK5CYII="
                        />
                      </defs>
                    </svg>
                  </div>

                  {/* Refer a Friend Button */}
                  <button
                    onClick={onReferFriend}
                    style={{
                      width: `${Math.round(298.89 * (screenWidth >= 1440 ? 1 : scaleFactor))}px`, // Figma: 298.89px
                      height: "4rem", // 64px - Fixed height from Figma
                      padding: `${Math.round(14.22 * (screenWidth >= 1440 ? 1 : scaleFactor))}px ${Math.round(28.44 * (screenWidth >= 1440 ? 1 : scaleFactor))}px`, // Exact Figma padding
                      backgroundColor: "#000000", // Pure black from Figma
                      color: "white",
                      border: "none",
                      borderRadius: "1.25rem", // 20px - Exact Figma border radius
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: `${screenWidth >= 1440 ? 25.28 : Math.round(25.28 * scaleFactor)}px`, // Exact Figma font size
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      gap: "0.5rem", // 8px gap between elements from Figma
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "#333333";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "#000000";
                    }}
                  >
                    <span style={{ fontSize: "1.25rem" }}>🏆</span>
                    <span>Refer a Friend</span>
                    <svg
                      width="26"
                      height="26"
                      viewBox="0 0 26 26"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{
                        width: "1.625rem", // 26px exact from Figma
                        height: "1.625rem", // 26px exact from Figma
                        filter: "brightness(0) invert(1)", // White color
                      }}
                    >
                      <rect
                        width="26"
                        height="26"
                        fill="url(#pattern0_more_figma)"
                      />
                      <defs>
                        <pattern
                          id="pattern0_more_figma"
                          patternContentUnits="objectBoundingBox"
                          width="1"
                          height="1"
                        >
                          <use
                            href="#image0_more_figma"
                            transform="scale(0.01)"
                          />
                        </pattern>
                        <image
                          id="image0_more_figma"
                          width="100"
                          height="100"
                          preserveAspectRatio="none"
                          href="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGQAAABkCAYAAABw4pVUAAAACXBIWXMAAAsTAAALEwEAmpwYAAABIklEQVR4nO3dMU7EMABFwT0SXIdut+IGtFycfQghCqSlzheZ6dxZebHTRPblAgAAAAAAwGGqJ49/RPVe3avXo+dyen3H+CHKkarn6qPfvsa307+pB0a5PohipRwpUfYkyp5E2ZMoexJlT6LsSZQ9ibInUfYkyp5E2ZMoexJlT6LsSZQ9ibInUfYkyp5E2ZMoexJlT6LsSZQ9ibInUfYkyp5E2VPd/vjB+3r03E6rxyvla/xy9NxOKUF2ZMvakY/6jsTYkRg7EmNHYuxIjB2JsSMxdiTGjsTYkRg7EmNHYuxIjB2JsSMxdiTGjsTYkRg7EmNHYuxIjB2JsSMxdiTGjsTYkRg7EmNHDuPfU705hH8zyt2FLkNy5REAAAAAAMDlv/kEAny3FazsZ1QAAAAASUVORK5CYII="
                        />
                      </defs>
                    </svg>
                  </button>
                </div>

                {/* Queue Position Display */}
                <div
                  style={{
                    marginTop: "1rem", // 16px
                    padding: "0.75rem 1rem", // 12px 16px
                    backgroundColor: "rgba(255, 255, 255, 0.25)", // Increased opacity for better readability
                    borderRadius: "0.5rem", // 8px
                    color: "white", // White text for better readability
                    fontSize: "0.875rem", // 14px
                    fontFamily: "Inter, sans-serif",
                    textAlign: "center",
                    maxWidth: "40rem", // Matches form container width
                    backdropFilter: "blur(4px)", // Additional blur for better readability
                  }}
                >
                  You're position{" "}
                  <span style={{ fontWeight: 600, color: "white" }}>
                    #{queuePosition.toLocaleString()}
                  </span>{" "}
                  in the waitlist
                </div>

                {/* Sign up another email in this browser (friend / testing) */}
                <SessionSwitchLink onClick={onUseDifferentEmail}>
                  Not you? Use a different email
                </SessionSwitchLink>
              </div>
            )}
          </div>
        </div>

        {/* Right Container - Image Stack */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <img
            src="/desktop-image-stack.png"
            alt="FootyFlowStates App Preview"
            style={{
              width: `${dimensions.imageStack.width}px`,
              height: `${dimensions.imageStack.height}px`,
              objectFit: "contain",
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default DesktopHeroLayout;
